import React, { ReactNode, useImperativeHandle, useState } from 'react';
import { ArrowNext, ArrowPrev } from '../ui/Swiper/styled';
import { SwiperContainer, SwiperTrack, SwiperSlide } from './styled';
import { Photo } from '../../graphql/graphql';

export interface SwiperHandle {
  slideNext: () => void;
  slidePrev: () => void;
}

interface SwiperProps {
  images?: Photo[];
  renderItem: (image: Photo, index: number) => ReactNode;
}

export const Swiper = React.forwardRef<SwiperHandle, SwiperProps>(
  ({ images, renderItem }, ref) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [startX, setStartX] = useState<number | null>(null);
    const imagesLength = images?.length || 0;

    const slideNext = () => {
      const nextIndex = (currentIndex + 1) % imagesLength;
      setCurrentIndex(nextIndex);
    };

    const slidePrev = () => {
      const prevIndex = (currentIndex - 1 + imagesLength) % imagesLength;
      setCurrentIndex(prevIndex);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
      setStartX(e.touches[0].clientX);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (!startX) return;

      const endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      const THRESHOLD = 30;

      if (diffX > THRESHOLD) slideNext();
      if (diffX < -THRESHOLD) slidePrev();

      setStartX(null);
    };

    useImperativeHandle(ref, () => ({ slideNext, slidePrev }), []);

    return (
      <SwiperContainer
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <SwiperTrack $offset={currentIndex * 100}>
          {images?.map((image, index) => (
            <SwiperSlide key={index}>{renderItem(image, index)}</SwiperSlide>
          ))}
        </SwiperTrack>
        <ArrowPrev onClick={slidePrev} />
        <ArrowNext onClick={slideNext} />
      </SwiperContainer>
    );
  }
);

Swiper.displayName = 'Swiper';
